import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { createLocation, getLocations } from '../../api/master'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { BackLink, DocHeader } from '../../components/PageParts'
import OnboardingContinuePrompt from '../../components/onboarding/OnboardingContinuePrompt'
import OnboardingStepBar from '../../components/onboarding/OnboardingStepBar'
import { useToast } from '../../components/Toast.jsx'
import { AddressFields, Field, useOnboardingStep } from './masterShared'
import { taxOn } from './LocationList'
import { returnTo } from '../../components/CreateOption'

const BLANK = {
  name: '', location_type: 'Store', address: '', city: '', state: '', postal_code: '', country: '',
  tax_enabled: true, tax_name: 'Sales Tax', tax_rate: '',
}

// One store or warehouse: where it is and the sales tax charged there.
export default function LocationForm() {
  const { locationId } = useParams()
  const isNew = !locationId
  const nav = useNavigate()
  // Opened from "+ Add a new …" in a dropdown: go back there afterwards.
  const done = returnTo(useLocation().search, '/master/locations')
  const qc = useQueryClient()
  const { notify } = useToast()
  const onboarding = useOnboardingStep('locations')
  const [form, setForm] = useState(BLANK)
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(!isNew)

  useEffect(() => {
    if (isNew) { setForm(BLANK); return }
    setLoading(true)
    getLocations()
      .then((rows) => {
        const l = (rows || []).find((r) => String(r.location_id) === String(locationId))
        if (!l) { setError('This location wasn’t found.'); return }
        setForm({
          name: l.name || '', location_type: l.location_type || 'Store', address: l.address || '', city: l.city || '',
          state: l.state || '', postal_code: l.postal_code || '', country: l.country || '',
          tax_enabled: taxOn(l), tax_name: l.tax_name || 'Sales Tax', tax_rate: l.tax_rate != null ? String(Number(l.tax_rate)) : '',
        })
      })
      .catch((e) => setError(e.message || 'Couldn’t load the location.'))
      .finally(() => setLoading(false))
  }, [locationId, isNew])

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  const save = async () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Give the location a name.'
    if (form.tax_enabled) {
      if (!form.tax_name.trim()) e.tax_name = 'Name the tax, e.g. Sales Tax or VAT.'
      const r = Number(form.tax_rate)
      if (String(form.tax_rate).trim() === '' || !Number.isFinite(r) || r < 0 || r > 20) e.tax_rate = 'Between 0 and 20%.'
    }
    setErrors(e)
    if (Object.keys(e).length) { setError('Check the highlighted fields.'); return }
    setSaving(true)
    setError('')
    try {
      await createLocation({
        location_id: isNew ? undefined : Number(locationId),
        name: form.name.trim(), location_type: form.location_type, address: form.address.trim(), city: form.city.trim(),
        state: form.state.trim(), postal_code: form.postal_code.trim(), country: form.country.trim(),
        tax_enabled: form.tax_enabled, tax_name: form.tax_name.trim() || 'Sales Tax', tax_rate: form.tax_enabled ? Number(form.tax_rate) : 0,
      })
      await qc.invalidateQueries({ queryKey: ['locations'] })
      notify(isNew ? `${form.name.trim()} added` : 'Location saved')
      if (onboarding.afterSave()) { if (isNew) { setForm(BLANK); setErrors({}) } return }
      nav(done)
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Couldn’t save the location.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="ci-form-page"><Spinner label="Loading location…" /></div>

  return (
    <div className="ci-form-page">
      <OnboardingStepBar step="locations" />
      <DocHeader
        back={<BackLink to={done} label={done === '/master/locations' ? 'Locations' : 'Back'} />}
        title={isNew ? 'New location' : (form.name || 'Location')}
        meta={isNew ? 'A store or warehouse where you keep stock.' : form.location_type}
      />
      {error && <ErrorBanner message={error} />}
      <div className="ci-form-meta-card mf-form">
        <div className="ci-form-meta-top two">
          <Field label="Name" error={errors.name}>
            <input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Main store" />
          </Field>
          <Field label="Type">
            <select value={form.location_type} onChange={(e) => set({ location_type: e.target.value })}>
              <option>Store</option>
              <option>Warehouse</option>
              <option>Other</option>
            </select>
          </Field>
        </div>
        <AddressFields form={form} set={set} />
      </div>

      <div className="ci-form-meta-card mf-form">
        <div className="mf-card-title">Sales tax <HelpTip text="Charged on taxable sales at this location. A product or service can have its own rate instead; one marked not taxable is never taxed." /></div>
        <div className="mf-checks">
          <label className="ci-form-toggle mf-check">
            <input type="checkbox" checked={form.tax_enabled} onChange={(e) => set({ tax_enabled: e.target.checked })} />
            Charge sales tax here
          </label>
        </div>
        {form.tax_enabled && (
          <div className="ci-form-meta-top two">
            <Field label="Tax name" error={errors.tax_name} hint="As printed on receipts.">
              <input value={form.tax_name} onChange={(e) => set({ tax_name: e.target.value })} placeholder="Sales Tax" />
            </Field>
            <Field label="Rate (%)" error={errors.tax_rate}>
              <input value={form.tax_rate} onChange={(e) => set({ tax_rate: e.target.value })} inputMode="decimal" placeholder="e.g. 7.5" />
            </Field>
          </div>
        )}
      </div>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={save}><Save size={14} />{saving ? 'Saving…' : (isNew ? 'Add location' : 'Save')}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav(done)}>Cancel</button>
        </div>
      </div>
      <OnboardingContinuePrompt open={onboarding.open} saved="Location saved" another="Add another location" next={onboarding.after} onAddAnother={onboarding.addAnother} onNext={onboarding.next} />
    </div>
  )
}
